
import React from "react";
import Header from "@/components/Header";
import InventoryDashboard from "@/components/InventoryDashboard";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Bell, TrendingDown, Sparkles, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEffect, useState } from "react";

const Index = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const userName = localStorage.getItem("userName");
    setIsLoggedIn(!!userName);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Utah Whiskey Tracker",
    "description": "Track whiskey availability in Utah state liquor stores and get instant alerts when your favorite bottles become available",
    "url": "https://utahwhiskeytracker.com",
    "applicationCategory": "Lifestyle",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-whiskey-light to-background">
      <SEO
        title="Utah State Liquor Store Tracker - Utah Whiskey Tracker"
        description="Track inventory at every state liquor store in Utah. Get instant alerts when whiskey becomes available at Utah DABS state liquor stores. Never miss limited releases again."
        keywords="state liquor store utah, utah state liquor store, DABS, utah whiskey, whiskey tracker, utah liquor, whiskey alerts, bourbon tracker, utah alcohol, DABS inventory"
        canonical="https://utahwhiskeytracker.com"
        structuredData={structuredData}
      />
      <Header />
      <div className="container mx-auto p-4">
        <div className="max-w-4xl mx-auto mt-4 mb-8">
          <div className="bg-destructive/10 border-2 border-destructive rounded-xl p-6 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-destructive mb-2">
              We've Closed Up Shop
            </h2>
            <p className="text-base md:text-lg text-foreground">
              Unfortunately, due to a lack of subscribers, Utah Whiskey Tracker has shut down operations. Account creation and login have been disabled. Thank you to everyone who supported us along the way!
            </p>
          </div>
        </div>
        <div className="max-w-4xl mx-auto text-center mb-12 pt-4">
          <h1 className="text-3xl md:text-5xl font-bold mb-4 text-whiskey-dark">
            Track Every State Liquor Store in Utah
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Monitor inventory at all Utah state liquor stores, get instant alerts, and never miss your favorite whiskey releases at DABS locations.
          </p>
        </div>
        
        {/* Teaser section removed - service has shut down */}

        {/* Search Section */}
        <div className="max-w-4xl mx-auto mb-10">
          <div className="bg-white border border-[#FFB347] rounded-xl p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-whiskey-dark">Try It Yourself</h2>
              <p className="text-muted-foreground mt-2">
                Search the current Utah DABS inventory to see what's available right now.
              </p>
            </div>
            <InventoryDashboard />
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-[#FFB347] rounded-xl p-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-whiskey-dark">Frequently Asked Questions</h2>
              <p className="text-muted-foreground mt-2">
                Everything you need to know about tracking whiskey in Utah
              </p>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="what-is-utahwhiskeytracker">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  What is Utah Whiskey Tracker and how does it work?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Utah Whiskey Tracker is an inventory monitoring and alert system that tracks product inventory at all Utah DABS liquor stores throughout the state. Users can setup alerts to receive notifications as soon as an item they're tracking comes back in stock or drops in price. Utah Whiskey Tracker also provides an easily filtered list of the Utah DABS Monthly Allocated Items list, as well as notifications for when the state opens a new bottle lottery for the extremely rare and limited liquor releases.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="how-alerts-work">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  How do the Whiskey-Tracker alerts work?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Our alert system monitors the Utah DABS inventory in real-time. When you create an alert for specific whiskey brands or bottles, we'll notify you instantly via SMS when those items become available at any DABS location. You can set alerts for back-in-stock notifications, price drops, or new product arrivals matching your search terms. You can also receive an alert to notify you when the monthly allocated items list is made available.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="allocated-items">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  What are allocated items and how can I get them?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Allocated items are rare, limited-release spirits that Utah DABS distributes through a limited number of their liquor stores on the third Saturday of each month. These include highly sought-after bottles like E H Taylor, Weller, Stagg, Old Fitzgerald, and other premium releases. We track when the allocated list is published each month and can alert you as soon as the list is made public so you know which stores will have which products.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="best-stores">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  Which Utah DABS stores get the best whiskey selection?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  While all state liquor stores in Utah receive inventory at different times, the monthly allocated items are only released at select locations across the state. Larger state liquor stores - like the Salt Lake City Wine Store, Park City, and Cottonwood Heights - tend to receive more premium releases than smaller locations. However, this varies month by month and rare bottles can appear at any Utah state liquor store - often getting dropped at 3 or 4 stores silently without warning, which is why our statewide tracking system monitors all Utah DABS locations simultaneously.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="pricing">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  Is Utah Whiskey Tracker free to use?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes! You can search the current DABS inventory for free and see what is in stock right now. We also offer subscription plans that include instant SMS notifications, priority access to allocated item announcements, and the option to create your own list of keyword searches you can save to track your favorite bottles. Our goal is to help every whiskey lover in Utah know the minute that one of their favorite bottles becomes available for purchase.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="notification-speed">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  How fast are the notifications?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Our system checks Utah DABS inventory every 10 minutes and sends notifications within minutes of detecting changes.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="state-liquor-stores">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  How do Utah state liquor stores work?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Utah operates a state-controlled liquor system where all spirits must be sold through state liquor stores managed by Utah DABS (Department of Alcoholic Beverage Services). There are over 40 state liquor store locations across Utah, each with varying inventory. Unlike private liquor stores in other states, all Utah state liquor stores have standardized pricing and are government-operated, making inventory tracking across all locations essential for finding rare whiskey.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="what-is-dabs">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  What is the Utah DABS and how does it work?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Utah DABS (Department of Alcoholic Beverage Services) is a state-controlled system that manages all alcohol sales in Utah. All spirits, including whiskey and bourbon, must be purchased through DABS stores or restaurants with proper licenses. The inventory is updated regularly, and rare bottles often sell out quickly, making tracking essential for whiskey enthusiasts.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="utah-liquor-laws">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  What should I know about buying whiskey in Utah?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Utah has unique liquor laws: all spirits must be purchased from DABS stores (no private liquor stores), stores are closed on Sundays and holidays, and there are quantity limits on some rare items. Prices are set by the state and are the same at all locations. You must be 21+ with valid ID, and some rare releases may have purchase limits per customer.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="popular-brands">
                <AccordionTrigger className="text-left font-semibold text-whiskey-dark">
                  What are the most tracked whiskey brands in Utah?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  The most popular tracked brands include Buffalo Trace products (Eagle Rare, E H Taylor, Blanton's, George T. Stagg, Weller), some Japanese whiskeys (Hibiki, Yamazaki), Pappy Van Winkle, Old Fitzgerald, Angel's Envy, and various craft distillery releases. Bourbon and rye whiskeys tend to be the most sought-after, especially anything allocated or with limited availability.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
